import React, { useRef, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faFileMagnifyingGlass,
  faMoneyCheckPen,
  faUser,
  faUsers
} from '@fortawesome/pro-regular-svg-icons';
import Divider from 'src/components/Divider';
import {
  SubmissionStepKey,
  useSubmissionStepsConfig
} from 'src/hooks/useSubmissionStepsConfig';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useFormContext } from 'src/contexts/FormContext';

interface Props {
  currentStep: number;
  onNavigate: (step: number) => void;
  enableConsolidatedPdf?: boolean;
  /** The journal runs integrity checks for authors, so the preview has a panel for them. */
  hasIntegrityChecks?: boolean;
}

type ISidebarSection = {
  id: string;
  label: string;
  step: number;
  /** Only set when the heading's own id differs from its label. */
  targetId?: string;
};

const sidebarStepKeys: SubmissionStepKey[] = [
  'details',
  'author',
  'preference',
  'preview'
];

export const iconMap: Partial<Record<SubmissionStepKey, IconDefinition>> = {
  details: faFileAlt,
  author: faUsers,
  preference: faMoneyCheckPen,
  preview: faFileMagnifyingGlass
};

export const SubmissionSidebar: React.FC<Props> = ({
  currentStep,
  onNavigate,
  enableConsolidatedPdf = false,
  hasIntegrityChecks = false
}) => {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const { steps } = useSubmissionStepsConfig();
  const { formData } = useFormContext();
  const detailsStepTitle =
    steps.find(step => step.stepKey === 'details')?.displayName ??
    'Submission Information';
  const authorStepTitle =
    steps.find(step => step.stepKey === 'author')?.displayName ??
    'Author Information';
  const preferenceStepTitle =
    steps.find(step => step.stepKey === 'preference')?.displayName ??
    'Submission Preferences';

  const [isActiveText, setIsActiveText] = useState<string>('');
  const groups = steps
    .map((stepItem, originalIndex) => {
      const componentName = stepItem.displayName;

      if (!componentName || !sidebarStepKeys.includes(stepItem.stepKey))
        return null;

      return { stepItem, originalIndex };
    })
    .filter(
      (g): g is { stepItem: (typeof steps)[number]; originalIndex: number } =>
        g !== null
    )
    .map((g, index) => {
      const { stepItem, originalIndex } = g;

      const componentName = stepItem.displayName as string;
      const title = componentName;
      const iconName = iconMap[stepItem.stepKey];

      // Step number is based on filtered index
      const stepNumber = index + 1;

      let sections: ISidebarSection[] = [];

      if (stepItem.stepKey === 'author') {
        sections = [
          {
            id: 'author-list',
            label: 'Author List',
            step: stepNumber
          },
          {
            id: 'submission-management',
            label: 'Submission Management',
            step: stepNumber
          }
        ];
      } else if (stepItem.stepKey === 'preview') {
        sections = [
          // The checks panel sits above the rest of the review, so link to it
          // in the order the author meets it.
          ...(hasIntegrityChecks
            ? [
                {
                  id: 'integrity-checks',
                  label: 'Integrity Checks',
                  step: stepNumber,
                  targetId: 'integrity-checks'
                }
              ]
            : []),
          {
            id: 'submission-information',
            label: detailsStepTitle,
            step: stepNumber
          },
          {
            id: 'author-information',
            label: authorStepTitle,
            step: stepNumber
          },
          {
            id: 'submission-preferences',
            label: preferenceStepTitle,
            step: stepNumber
          },
          ...(enableConsolidatedPdf
            ? [
                {
                  id: 'consolidated-submission-file',
                  label: 'Consolidated Submission File',
                  step: stepNumber
                }
              ]
            : [])
        ];
      } else {
        const seenLabels = new Set<string>();

        sections = Object.entries(stepItem.configs || {})
          .filter(([_, cfg]) => {
            if (cfg?.component === 'Custom:Checkbox') {
              return true;
            }

            if (!cfg?.params?.labels?.main?.trim()) return false;
            if (!cfg) return false;
            if (
              formData.metadata?.articleLanguage === 'English' &&
              cfg.params?.externalFieldDependency
            )
              return false;
            const hasExternal =
              cfg.params?.externalFieldDependency && !cfg.params?.isMainField;
            return !hasExternal;
          })
          .map(([key, cfg]) => {
            const rawLabel = cfg?.params?.labels?.main?.trim();
            const label =
              rawLabel === 'Generated Dataset' ||
              rawLabel === 'Utilized Dataset'
                ? 'Datasets Statement'
                : rawLabel !== ''
                ? rawLabel
                : 'Confirmations';
            return {
              id: key.toLowerCase().replace(/\s+/g, '-'),
              label,
              step: stepNumber
            };
          })
          .filter(section => {
            if (seenLabels.has(section.label)) return false;
            seenLabels.add(section.label);
            return true;
          });
      }
      return {
        id: componentName.toLowerCase(),
        title,
        step: stepNumber,
        originalIndex,
        sections,
        iconName
      };
    });

  const scrollToTop = () => window.scrollTo({ top: 0 });
  return (
    <>
      {/* Mobile: horizontal scrollable step indicator */}
      <Box display={{ base: 'block', md: 'none' }}>
        <Box overflowX="auto" pb={1}>
          <Flex gap={0} minWidth="max-content" alignItems="stretch">
            {groups.map((group, index) => (
              <React.Fragment key={group.id}>
                <Flex
                  direction="column"
                  alignItems="center"
                  gap={1}
                  px={3}
                  py={2}
                  cursor={group.step > currentStep ? 'not-allowed' : 'pointer'}
                  opacity={group.step > currentStep ? 0.5 : 1}
                  borderBottom="3px solid"
                  borderColor={
                    group.step === currentStep
                      ? 'actionSecondaryContent'
                      : 'transparent'
                  }
                  color={
                    group.step === currentStep
                      ? 'actionSecondaryContent'
                      : undefined
                  }
                  onClick={() => {
                    if (group.step <= currentStep) {
                      onNavigate(group.originalIndex);
                      scrollToTop();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={group.iconName ?? faUser} />
                  <Text
                    textStyle="textContext"
                    fontSize="xs"
                    whiteSpace="nowrap"
                    fontWeight={
                      group.step === currentStep ? 'bold' : undefined
                    }
                  >
                    {group.title}
                  </Text>
                </Flex>
                {index < groups.length - 1 && (
                  <Flex alignItems="center">
                    <Box w="20px" h="1px" bgColor="neutralPrimaryBorder" />
                  </Flex>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Desktop: accordion sidebar (unchanged) */}
      <VStack align="stretch" spacing={2} display={{ base: 'none', md: 'flex' }}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingItem">New Submission</Text>
          {Divider}
        </Flex>
        <Accordion allowToggle defaultIndex={[currentStep - 1]}>
          {groups.map(group => (
            <AccordionItem
              key={group.id}
              border="none"
              isDisabled={group.step > currentStep}
            >
              {() => (
                <>
                  <AccordionButton
                    borderLeft={
                      group.step === currentStep ? '3px solid' : '3px solid'
                    }
                    borderColor={
                      group.step === currentStep
                        ? 'actionSecondaryContent'
                        : 'transparent'
                    }
                    onClick={() => {
                      onNavigate(group.originalIndex);
                      scrollToTop();
                    }}
                    _disabled={{
                      opacity: 1,
                      cursor: 'not-allowed'
                    }}
                  >
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      color={
                        group.step === currentStep
                          ? 'actionSecondaryContent'
                          : undefined
                      }
                      gap={1}
                      whiteSpace="nowrap"
                    >
                      <FontAwesomeIcon icon={group.iconName ?? faUser} />
                      <Text textStyle="headingItem">{group.title}</Text>
                    </Flex>
                  </AccordionButton>
                  <AccordionPanel pe={3}>
                    <Box
                      borderLeft="1px solid"
                      borderColor="neutralPrimaryBorder"
                      pl={3}
                    >
                      <VStack align="stretch" spacing={1}>
                        {group.sections.map(section => {
                          const target = document.getElementById(
                            section.targetId ?? section.label
                          );
                          return (
                            <Box
                              key={section.id}
                              ref={el => (refs.current[section.id] = el)}
                              px={3}
                              py={1}
                              cursor="pointer"
                              _focus={{
                                boxShadow: 'outline',
                                bg: 'surfaceSecondaryFill'
                              }}
                              _hover={{ bg: 'surfaceSecondaryFill' }}
                              onClick={() => {
                                setIsActiveText(section.label);
                                if (target) {
                                  const headerOffset = 90;
                                  const y =
                                    target.getBoundingClientRect().top +
                                    window.pageYOffset -
                                    headerOffset;

                                  window.scrollTo({
                                    top: y,
                                    behavior: 'smooth'
                                  });
                                }
                              }}
                            >
                              <Text
                                textStyle={'textContext'}
                                fontWeight={
                                  isActiveText === section.label
                                    ? 'bold'
                                    : undefined
                                }
                              >
                                {section.label}
                              </Text>
                            </Box>
                          );
                        })}
                      </VStack>
                    </Box>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </VStack>
    </>
  );
};
